import React, { FormEvent, useCallback, useRef, useState } from 'react'
import { Button, ButtonGroup, ClickAwayListener, Grow, Icon, MenuItem, MenuList, Paper, Popper, TextField, makeStyles, useObservable } from '@cicero/ui'
import { AttributeDoc, AttributeOptionDoc, AttributeValueSchema, PageDoc, PageSchema, PageTemplateDoc, PageTemplateSchema, useModel } from 'models'

/**
 * A small form to create a page with just a title
 */
export const PageQuickCreateForm = ({
  parent_id,
  template_id,
  buttonText,
  attributes,
  afterCreate,
}: {
  parent_id?: string
  template_id?: string
  buttonText?: string
  attributes?: (AttributeOptionDoc|IAttributeValueSetter)[]
  afterCreate?: (page: PageDoc) => void
}) => {
  const { quickCreateForm } = useStyles()
  const [title, setTitle] = useState('')
  const [showButton, setShowButton] = useState(true)
  const Page = useModel(PageSchema)
  const AttributeValues = useModel(AttributeValueSchema)

  const createPage = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const params: any = {}
    if (parent_id) { params.parent_id = parent_id }
    if (template_id) { params.template_id = template_id }
    const p = await Page.CREATE({ title, ...params })
    setTitle('')
    setShowButton(true)
    if (afterCreate) { afterCreate(p) }
    if (attributes && p.id) {
      for (const toCreate of attributes) {
        if ('attr' in toCreate) {
          const { attr, value } = toCreate as any
          AttributeValues.CREATE({ page_id: p.id, attribute_id: attr.id, value })
        } else {
          const opt: AttributeOptionDoc = toCreate as any
          AttributeValues.CREATE({ page_id: p.id, attribute_id: opt.attribute_id, option_id: opt.id })
        }
      }
    }
  }, [title, Page])


  return (
    <form className={quickCreateForm} onSubmit={createPage}>
      { buttonText && showButton && title.length === 0
        ? <Button onClick={() => setShowButton(false)}>{buttonText}</Button>
        : <TextField
          label="Quick Create Page"
          required
          autoFocus={true}
          autoComplete="off"
          value={title}
          onChange={e => setTitle(e.target.value)}
          onBlur={() => setShowButton(true)}
        />
      }
    </form>
  )
}

/**
 * A button to add a page, and a menu of current templates to potentially use
 */
export const QuickAddPageToTemplate = () => {
  const [open, setOpen] = useState(false)
  const Pages = useModel(PageSchema)
  const Templates = useModel(PageTemplateSchema)
  const templates = useObservable(() => Templates.QUERY$(), [], [])
  const anchorRef = useRef<HTMLDivElement>(null)

  const createPage = async (template?: PageTemplateDoc) => {
    setOpen(false)
    const page = await Pages.NEW(template ? { template_id: template.id } : {})
    Pages.active$.next(page)
  }

  return <>
    <ButtonGroup variant="contained" color="primary" ref={anchorRef} aria-label="split button">
      <Button onClick={() => { createPage() }}>Add Page</Button>
      <Button
        color="primary"
        size="small"
        aria-controls={open ? 'split-button-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-label="select merge strategy"
        aria-haspopup="menu"
        onClick={() => setOpen(!open)}
      >
        <Icon>arrow_drop_down</Icon>
      </Button>
    </ButtonGroup>
    <Popper open={open} anchorEl={anchorRef.current} role={undefined} transition disablePortal>
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
          }}
        >
          <Paper>
            <ClickAwayListener onClickAway={() => setOpen(false)}>
              <MenuList id="split-button-menu">
                {templates.map((template) => (
                  <MenuItem key={template.id} onClick={() => { createPage(template) }}>{template.name}</MenuItem>
                ))}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  </>
}

const useStyles = makeStyles({
  quickCreateForm: {
    display: 'flex',
    padding: 6,
    '& > .MuiFormControl-root': {
      flexGrow: 1,
    },
  },
})

interface IAttributeValueSetter {
  attr: AttributeDoc
  value: any
}
